<!--
SimpleNewsList Component - Without Pinia Store
-->

<script setup>
import { ref } from 'vue';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Skeleton from 'primevue/skeleton';
import Message from 'primevue/message';

const props = defineProps({
    items: {
        type: Array,
        default: () => []
    },
    loading: {
        type: Boolean,
        default: false
    },
    error: {
        type: String,
        default: null
    }
});

// Modal for news detail
const showNewsModal = ref(false);
const selectedNews = ref(null);

function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}

function openNews(item) {
    // Track click via API if needed
    if (item.id) {
        fetch('/api/overview/track', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
            },
            body: JSON.stringify({
                type: 'news_click',
                id: item.id
            })
        }).catch(() => {
            // Silently fail tracking
        });
    }

    // Open modal with news detail
    selectedNews.value = item;
    showNewsModal.value = true;
}

function truncateText(text, maxLength = 150) {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
}

function getPinnedSeverity() {
    return 'warning';
}
</script>

<template>
    <div class="news-list">
        <!-- Loading State -->
        <template v-if="loading">
            <div v-for="i in 3" :key="`skeleton-${i}`" class="mb-3">
                <Card>
                    <template #content>
                        <div class="flex gap-3">
                            <Skeleton width="6rem" height="6rem" />
                            <div class="flex-1">
                                <Skeleton width="70%" height="1.5rem" class="mb-2" />
                                <Skeleton width="100%" height="1rem" class="mb-2" />
                                <Skeleton width="30%" height="0.8rem" />
                            </div>
                        </div>
                    </template>
                </Card>
            </div>
        </template>

        <!-- Error State -->
        <Message v-else-if="error" severity="error" :closable="false">
            {{ error }}
        </Message>

        <!-- Empty State -->
        <div v-else-if="!items || items.length === 0" class="text-center py-5">
            <i class="pi pi-inbox text-4xl text-color-secondary mb-3"></i>
            <p class="text-color-secondary">No news available at this time</p>
        </div>

        <!-- News Items -->
        <template v-else>
            <div
                v-for="item in items"
                :key="item.id"
                class="news-item mb-3"
                @click="openNews(item)"
            >
                <Card class="cursor-pointer hover:shadow-3 transition-shadow transition-duration-200">
                    <template #content>
                        <div class="flex gap-3">
                            <!-- News Image -->
                            <div v-if="item.image_url" class="news-image">
                                <img
                                    :src="item.image_url"
                                    :alt="item.title"
                                    class="news-image object-contain border-round"
                                    loading="lazy"
                                />
                            </div>

                            <!-- News Content -->
                            <div class="flex-1 min-w-0">
                                <div class="flex align-items-center gap-2 mb-2">
                                    <h3 class="text-lg font-semibold m-0 text-color">
                                        {{ item.title }}
                                    </h3>
                                </div>

                                <p v-if="item.excerpt" class="text-sm text-color-secondary m-0 mb-2 line-clamp-2">
                                    {{ truncateText(item.excerpt) }}
                                </p>

                                <div class="flex align-items-center gap-3 text-sm text-color-secondary">
                                    <span class="flex align-items-center gap-1">
                                        <i class="pi pi-calendar text-xs"></i>
                                        {{ formatDate(item.publish_at || item.created_at) }}
                                    </span>
                                    <span v-if="item.author" class="flex align-items-center gap-1">
                                        <i class="pi pi-user text-xs"></i>
                                        {{ item.author.name || 'System' }}
                                    </span>
                                    <span v-if="item.reading_time" class="flex align-items-center gap-1">
                                        <i class="pi pi-clock text-xs"></i>
                                        {{ item.reading_time }} min read
                                    </span>
                                </div>

                                <!-- Tags -->
                                <div v-if="item.tags && item.tags.length > 0" class="flex gap-1 mt-2">
                                    <Tag
                                        v-for="tag in item.tags.slice(0, 3)"
                                        :key="tag.id"
                                        :value="tag.name"
                                        severity="secondary"
                                        class="text-xs"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>
                </Card>
            </div>
        </template>

        <!-- News Detail Modal -->
        <Dialog
            v-model:visible="showNewsModal"
            :header="selectedNews?.title"
            :style="{ width: '50rem' }"
            :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
            modal
            maximizable
        >
            <div v-if="selectedNews" class="news-detail">
                <!-- Hero Image -->
                <div v-if="selectedNews.image_url" class="mb-4">
                    <img
                        :src="selectedNews.image_url"
                        :alt="selectedNews.title"
                        class="w-full border-round"
                    />
                </div>

                <!-- Meta Info -->
                <div class="flex align-items-center gap-3 mb-3 text-sm text-color-secondary">
                    <span class="flex align-items-center gap-1">
                        <i class="pi pi-calendar"></i>
                        {{ formatDate(selectedNews.publish_at || selectedNews.created_at) }}
                    </span>
                    <span v-if="selectedNews.author" class="flex align-items-center gap-1">
                        <i class="pi pi-user"></i>
                        {{ selectedNews.author.name || 'System' }}
                    </span>
                    <span v-if="selectedNews.reading_time" class="flex align-items-center gap-1">
                        <i class="pi pi-clock"></i>
                        {{ selectedNews.reading_time }} min read
                    </span>
                </div>

                <!-- Categories -->
                <div v-if="selectedNews.categories && selectedNews.categories.length > 0" class="mb-3">
                    <Tag
                        v-for="category in selectedNews.categories"
                        :key="category.id"
                        :value="category.name"
                        severity="info"
                        class="mr-2"
                    />
                </div>

                <!-- Content -->
                <div class="news-body" v-html="selectedNews.body || selectedNews.excerpt"></div>

                <!-- Tags -->
                <div v-if="selectedNews.tags && selectedNews.tags.length > 0" class="mt-4 pt-3 border-top-1 border-300">
                    <span class="text-sm text-color-secondary mr-2">Tags:</span>
                    <Tag
                        v-for="tag in selectedNews.tags"
                        :key="tag.id"
                        :value="tag.name"
                        severity="secondary"
                        class="mr-2"
                    />
                </div>
            </div>

            <template #footer>
                <Button label="Close" @click="showNewsModal = false" />
            </template>
        </Dialog>
    </div>
</template>

<style scoped>
.news-item {
    animation: fadeIn 0.3s ease-in;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.news-detail :deep(h1),
.news-detail :deep(h2),
.news-detail :deep(h3) {
    margin-top: 1.5rem;
    margin-bottom: 1rem;
}

.news-detail :deep(p) {
    line-height: 1.6;
    margin-bottom: 1rem;
}

.news-detail :deep(ul),
.news-detail :deep(ol) {
    margin-bottom: 1rem;
    padding-left: 2rem;
}

.news-detail :deep(blockquote) {
    border-left: 4px solid var(--primary-color);
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.news-image {
    display: block;
    max-width: 200px;
    max-height: 200px;
}

</style>
